import { MutationTree } from 'vuex';
import { ErrorDetail } from '../../types';
import { ExternalProgramUsage, ExternalProgramUsageState, ExternalProgramUsageEdit } from './types';
import { Node } from '../node/types';
import * as types from './mutation-types';

// 外部プログラム利用情報のエラーをクリアする
const resetChangesAllErrors = (state: ExternalProgramUsageState): void => {
    const indexState = state.index;
    indexState.error = false;
    indexState.errorCode = undefined;
    indexState.errorMsg = undefined;

    const createState = state.create;
    createState.error = false;
    createState.errorTitle = undefined;
    createState.errorDetails = undefined;

    const showState = state.show;
    showState.error = false;
    showState.errorCode = undefined;
    showState.errorMsg = undefined;

    const updateState = state.update;
    updateState.error = false;
    updateState.errorTitle = undefined;
    updateState.errorDetails = undefined;

    const destroyState = state.destroy;
    destroyState.error = false;
    destroyState.errorTitle = undefined;
    destroyState.errorDetails = undefined;

    const csvState = state.csv;
    csvState.error = false;
    csvState.errorCode = undefined;
    csvState.errorMsg = undefined;

    const certificateFileDownloadState = state.certificateFileDownload;
    certificateFileDownloadState.error = false;
    certificateFileDownloadState.errorTitle = undefined;
    certificateFileDownloadState.errorDetails = undefined;
};

export const mutations: MutationTree<ExternalProgramUsageState> = {
    // 外部プログラム利用情報一覧系
    [types.INDEX_START](state) {
        resetChangesAllErrors(state);
        const indexState = state.index;
        indexState.error = false;
        indexState.errorCode = undefined;
        indexState.errorMsg = undefined;
        indexState.isProcessing = true;
        indexState.isFetchSuccess = false;
    },

    [types.INDEX_SUCCESS](state, payload: { count: number; external_program_usages: Array<ExternalProgramUsage> }) {
        state.count = payload.count;
        state.lists = payload.external_program_usages;

        const indexState = state.index;
        indexState.isProcessing = false;
        indexState.isFetchSuccess = true;
    },

    [types.INDEX_FAILURE](state, payload: { code: string; message: string }) {
        const indexState = state.index;
        indexState.errorCode = payload.code;
        indexState.errorMsg = payload.message;
        indexState.error = true;
        indexState.isProcessing = false;
        indexState.isFetchSuccess = false;
    },

    // 生成系
    [types.CLEAR_ERROR_EXTERNAL_PROGRAM_USAGE](state) {
        resetChangesAllErrors(state);
        const createState = state.create;
        createState.isCreated = false;
    },

    [types.CREATE_START](state) {
        resetChangesAllErrors(state);
        const createState = state.create;
        createState.isProcessing = true;
    },

    [types.CREATE_SUCCESS](state) {
        const createState = state.create;
        createState.isProcessing = false;
        createState.isCreated = true;
    },

    [types.CREATE_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const createState = state.create;
        createState.errorTitle = payload.errorTitle;
        createState.errorDetails = payload.errorDetails;
        createState.error = true;
        createState.isProcessing = false;
    },

    // 外部プログラム利用情報詳細系
    [types.SHOW_START](state) {
        resetChangesAllErrors(state);
        const showState = state.show;
        showState.isProcessing = true;
        showState.isFetchSuccess = false;
    },

    [types.SHOW_SUCCESS](state, payload: { external_program_usage: ExternalProgramUsageEdit; nodes: Array<Node> }) {
        const showState = state.show;
        state.details.externalProgramUsage = payload.external_program_usage;
        state.details.nodes = payload.nodes.length > 0 ? payload.nodes : [];
        showState.isProcessing = false;
        showState.isFetchSuccess = true;
    },

    [types.SHOW_FAILURE](state, payload: { code: string; message: string }) {
        const showState = state.show;
        showState.errorCode = payload.code;
        showState.errorMsg = payload.message;
        showState.error = true;
        showState.isProcessing = false;
        showState.isFetchSuccess = false;
    },

    // 外部プログラム利用情報更新系
    [types.UPDATE_START](state) {
        resetChangesAllErrors(state);
        const updateState = state.update;
        updateState.isProcessing = true;
    },

    [types.UPDATE_SUCCESS](state) {
        const updateState = state.update;
        updateState.isProcessing = false;
    },

    [types.UPDATE_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const updateState = state.update;
        updateState.errorTitle = payload.errorTitle;
        updateState.errorDetails = payload.errorDetails;
        updateState.error = true;
        updateState.isProcessing = false;
    },

    // 外部プログラム利用情報削除系
    [types.DESTROY_START](state) {
        resetChangesAllErrors(state);
        const destroyState = state.destroy;
        destroyState.isProcessing = true;
    },

    [types.DESTROY_SUCCESS](state) {
        const destroyState = state.destroy;
        destroyState.isProcessing = false;
    },

    [types.DESTROY_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const destroyState = state.destroy;
        destroyState.errorTitle = payload.errorTitle;
        destroyState.errorDetails = payload.errorDetails;
        destroyState.error = true;
        destroyState.isProcessing = false;
    },

    // CSV出力系
    [types.CSV_OUTPUT_START](state) {
        resetChangesAllErrors(state);
        const csvState = state.csv;
        csvState.isProcessing = true;
    },

    [types.CSV_OUTPUT_SUCCESS](state) {
        const csvState = state.csv;
        csvState.isProcessing = false;
    },

    [types.CSV_OUTPUT_FAILURE](state, payload: { code: string; message: string }) {
        const csvState = state.csv;
        csvState.errorCode = payload.code;
        csvState.errorMsg = payload.message;
        csvState.error = true;
        csvState.isProcessing = false;
    },

    // 証明書ファイルダウンロード系
    [types.CERTIFICATE_FILE_DOWNLOAD_START](state) {
        resetChangesAllErrors(state);
        const certificateFileDownloadState = state.certificateFileDownload;
        certificateFileDownloadState.isProcessing = true;
    },

    [types.CERTIFICATE_FILE_DOWNLOAD_SUCCESS](state) {
        const certificateFileDownloadState = state.certificateFileDownload;
        certificateFileDownloadState.isProcessing = false;
    },

    [types.CERTIFICATE_FILE_DOWNLOAD_FAILURE](
        state,
        payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }
    ) {
        const certificateFileDownloadState = state.certificateFileDownload;
        certificateFileDownloadState.errorTitle = payload.errorTitle;
        certificateFileDownloadState.errorDetails = payload.errorDetails;
        certificateFileDownloadState.error = true;
        certificateFileDownloadState.isProcessing = false;
    },

    // FATALエラー系
    [types.FATAL_FAILURE](state) {
        const indexState = state.index;
        indexState.isProcessing = false;
        indexState.isFetchSuccess = false;

        const createState = state.create;
        createState.isProcessing = false;
        createState.isCreated = false;

        const showState = state.show;
        showState.isProcessing = false;
        showState.isFetchSuccess = false;

        const updateState = state.update;
        updateState.isProcessing = false;

        const destroyState = state.destroy;
        destroyState.isProcessing = false;

        const csvState = state.csv;
        csvState.isProcessing = false;

        const certificateFileDownloadState = state.certificateFileDownload;
        certificateFileDownloadState.isProcessing = false;
    },

    [types.CLEAR_ALL](state) {
        state.lists = undefined;
        state.count = 0;
        state.details.externalProgramUsage = undefined;
        state.details.nodes = undefined;
    },
};
